/**
 * Handles modern/traditional pagination UI and infinite scroll.
 * Collection pages use data-pagination-style="infinite" (no crawlable page links).
 */
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.init();
      document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.infiniteScrollLoader = this.querySelector('.js-infinite-scroll-loader');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.controller = null;

      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);

      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      const hasMore = this.pagination && this.pagination.dataset.isMoreResults === 'true';
      const isInfiniteOnly = this.pagination?.dataset?.paginationStyle === 'infinite';

      if (hasMore && this.results) {
        if (this.loadMoreButton) {
          this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
        }

        if (this.infiniteScroll || isInfiniteOnly) {
          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.loadMore();
                  this.intersectingTimer = setInterval(this.loadMore.bind(this), 1500);
                } else if (this.intersectingTimer) {
                  clearInterval(this.intersectingTimer);
                }
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );

          this.paginationObserver.observe(this.pagination);
        }
      }
    }

    destroy() {
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.loadMoreButton) {
        this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      }
      this.removeEventListener('click', this.resultClickHandler);
      this.loadMoreClickHandler = null;
    }

    reload() {
      this.destroy();
      this.init();
    }

    getNextPageUrl() {
      if (this.loadMoreButton?.href) return this.loadMoreButton.href;
      if (this.loadMoreButton?.dataset?.nextUrl) return this.loadMoreButton.dataset.nextUrl;
      if (this.pagination?.dataset?.nextUrl) return this.pagination.dataset.nextUrl;
      return null;
    }

    isInfinitePagination() {
      return this.pagination?.dataset?.paginationStyle === 'infinite';
    }

    showInfiniteScrollLoader() {
      if (!this.infiniteScrollLoader) return;
      this.infiniteScrollLoader.hidden = false;
      this.infiniteScrollLoader.setAttribute('aria-hidden', 'false');
      this.infiniteScrollLoader.setAttribute('aria-busy', 'true');
    }

    hideInfiniteScrollLoader() {
      if (!this.infiniteScrollLoader) return;
      this.infiniteScrollLoader.hidden = true;
      this.infiniteScrollLoader.setAttribute('aria-hidden', 'true');
      this.infiniteScrollLoader.setAttribute('aria-busy', 'false');
    }

    removeInfiniteScroll() {
      const infiniteScroll = this.pagination?.closest('.collection-infinite-scroll');
      if (infiniteScroll) {
        infiniteScroll.remove();
        this.pagination = null;
        this.infiniteScrollLoader = null;
        return;
      }
      this.pagination?.remove();
      this.pagination = null;
    }

    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore();
    }

    handleResultClick(evt) {
      if (evt.target.closest('a') && evt.target.closest('.js-pagination-result') && this.controller) {
        this.controller.abort();
      }
    }

    async loadMore() {
      if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
        const nextUrl = this.getNextPageUrl();
        if (!nextUrl) return;

        this.isFetching = true;
        this.controller = new AbortController();

        try {
          if (this.loadMoreButton) {
            this.loadMoreButton.classList.add('is-loading');
            this.loadMoreButton.setAttribute('aria-disabled', 'true');
          }

          if (this.isInfinitePagination()) {
            this.showInfiniteScrollLoader();
          } else if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.add('is-loading');
          }

          const response = await fetch(nextUrl, {
            signal: this.controller.signal
          });

          if (!response.ok) throw new Error(response.status);
          if (this.dataset.pauseInfiniteScroll === 'true') {
            throw new Error('Infinite scroll was interrupted.');
          }

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          let newResultsHtml = '';
          const lastResult = this.results[this.results.length - 1];
          tmpl.content.querySelectorAll('.js-pagination-result').forEach((result) => {
            newResultsHtml += result.outerHTML;
          });
          lastResult.insertAdjacentHTML('afterend', newResultsHtml);
          window.initLazyImages();
          this.results = this.querySelectorAll('.js-pagination-result');

          const newPagination = tmpl.content.querySelector('.js-pagination');
          if (newPagination && newPagination.dataset.isMoreResults === 'true') {
            if (newPagination.dataset.paginationStyle === 'infinite') {
              this.pagination.dataset.nextUrl = newPagination.dataset.nextUrl;
              this.pagination.dataset.isMoreResults = 'true';
            } else {
              this.pagination.innerHTML = newPagination.innerHTML;
              if (this.loadMoreButton) {
                this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
              }
              this.loadMoreButton = this.querySelector('.js-pagination-load-more');
              if (this.loadMoreButton) {
                this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
              }
            }
          } else {
            this.removeInfiniteScroll();
            if (this.intersectingTimer) clearInterval(this.intersectingTimer);
            if (this.paginationObserver) this.paginationObserver.disconnect();
            document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
          }
        } catch (error) {
          console.log('Unable to fetch next page of results', error); // eslint-disable-line
          if (this.intersectingTimer) clearInterval(this.intersectingTimer);
        } finally {
          this.controller = null;

          if (this.loadMoreButton) {
            this.loadMoreButton.classList.remove('is-loading');
            this.loadMoreButton.removeAttribute('aria-disabled');
          }

          this.hideInfiniteScrollLoader();
          this.isFetching = false;

          if (this.pagination?.dataset?.paginationStyle === 'traditional') {
            this.pagination.classList.remove('is-loading');
          }
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
